import React, { type FC, memo, useCallback, useEffect, useState } from "react";
import { OrderContext } from "../../../context/OrderContext";
import "./ReviewOrder.scss";
import {
  ApplyToAllTread,
  ChooseLaterString,
  NO,
  getStepFastenerDisplayName,
  SameAsFloor,
  TreadValue,
  YES,
  getFeatureType,
  isFeatureRemovable,
} from "../Forms/Partials/Features/Feature/FeatureConstants";
import type { BaseDeckFormType } from "../Forms/Partials/Features/Feature/FeatureSchema";
import { chooseLaterString } from "../Forms/Partials/Product/ProductFormConstants";
import { Button, Confirm } from "semantic-ui-react";
import { FeatureType, OrderStatus } from "models";
import { OrderType, type ServerOrder } from "models/Order";
import { Modal as NativeModal } from "semantic-ui-react";
import { isOptionsHidden } from "../helpers/FeatureHelper";
import { useFeatureFlags } from "services";
import { useT } from "i18n";

interface ReviewOrderProps {
  deleteOrder: (order?: ServerOrder) => void;
  cancelQuote: () => void;
  hiddenLoading?: boolean;
}

// Title Review Quote/Order in Form Sidebar
/**
 * Renders field groups for each key from orderSchema
 * @param props
 * @constructor
 */
export const ReviewOrder = (props: ReviewOrderProps) => {
  const t = useT();
  const { state } = React.useContext(OrderContext);
  const flags = useFeatureFlags();
  const swatch = state.swatches.find(
    (swatch) =>
      swatch.color === state.product.color && swatch.pattern === state.product.fabric,
  );
  const isAfterQuote =
    state.order?.status === OrderStatus.QuotedOptions ||
    state.order?.status === OrderStatus.ReadyForOrder;
  const isAfterOrder = state.order?.status === OrderStatus.Ordered;
  const type = state.order?.type ?? state.project.type;
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const hasValueForShipping =
    state.shipping.whereToShip ||
    state.shipping.name ||
    state.shipping.addressLine1 ||
    state.shipping.addressLine2 ||
    state.shipping.city ||
    state.shipping.zip ||
    state.shipping.shippingState;
  const noteControl = (note: string) => {
    const list: JSX.Element[] = [];
    if (note && note != "null") {
      const noteList = note.split(". ");
      for (let index = 0; index < noteList.length; index++) {
        if (noteList[index].length !== 0) {
          list.push(
            <>
              <div>
                <ReviewField
                  value={noteList[index].length == 0 ? "null" : noteList[index]}
                  label={index == 0 ? "Field Notes" : "Feature Notes"}
                />{" "}
              </div>
            </>,
          );
        }
      }
    }
    return list;
  };

  const getTreadComment = (comment: string, feature: BaseDeckFormType) => {
    const controls = [];
    if (comment && comment.length > 0) {
      if (comment.includes(ApplyToAllTread)) {
        const index = comment.indexOf(ApplyToAllTread);
        if (comment.length > index) {
          const value = comment.substring(0, index);
          if ((feature?.measurement?.step_treads?.numTreads ?? 0) > 0) {
            for (
              let index = 0;
              index < (feature?.measurement?.step_treads?.numTreads ?? 0);
              index++
            ) {
              const title = TreadValue + (index + 1).toString();
              controls.push({
                key: title,
                value: value,
              });
            }
          }
        }
      } else {
        const treadInfoList = comment.split("\n");
        for (let index = 0; index < treadInfoList.length; index++) {
          const treadInfo = treadInfoList[index].split(":");
          if (treadInfo.length == 2) {
            const value = treadInfo[1].toString();
            const title = TreadValue + (index + 1).toString();
            controls.push({
              key: title,
              value: value,
            });
          }
        }
      }
    }

    return controls;
  };

  const handleDeleteOrder: any = useCallback(() => {
    setIsConfirmModalOpen(false);
    setLoading(true);
    props.deleteOrder(state.order);
  }, []);

  const orderPriceOption = state.order?.priceOption ?? undefined;

  const [priceOption, setPriceOption] = useState<string | undefined>(orderPriceOption);

  useEffect(() => {
    if (loading) {
      setLoading(false);
    }
  }, [props.hiddenLoading]);

  const totalYStrapQty =
    state.spec.fullPerimeterCoverAccessoryAttributes?.yStrapAssembly?.yStrapQty;

  return (
    <div className={"review-order"}>
      <div className={"review-order__fields review-order__fields--product"}>
        <div className={"review-order__sub-title"}>
          {t("component.reviewOrder.product", "Product")}
        </div>
        {type === OrderType.Cover && isAfterQuote && (
          <div
            style={{
              padding: "6px 8px",
              marginTop: "5px",
              border: "1px solid #f8f9fa",
              borderRadius: "6px",
              backgroundColor: "#f8f9fa",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <i
                className={"featureIcon exclamation circle icon"}
                style={{ marginBottom: "7px" }}
              />
              <span style={{ fontSize: "12px", fontWeight: "bold" }}>
                {t("component.reviewOrder.gridSpacingNotice", "Grid spacing notice")}
              </span>
            </div>
            <span style={{ fontSize: "11px" }}>
              {t(
                "component.reviewOrder.gridSpacingNoticeText",
                "You are responsible for selecting the appropriate grid spacing for the order (3X3 or 5X5). Please note: Latham does not guarantee a perfect anchor match, some anchors may need to be re-drilled.",
              )}
            </span>
          </div>
        )}
        {state.project.isDistributorDealer &&
          flags.dealerDistributor &&
          priceOption &&
          priceOption !==
            t("component.reviewOrder.noNeedToChoose", "No need to choose") && (
            <ReviewField
              value={priceOption}
              label={t("component.reviewOrder.priceOption", "Price Option")}
            />
          )}
        <ReviewField
          value={state.product.newOrReplacement}
          label={
            type === OrderType.Cover
              ? t("component.reviewOrder.replacementCover", "Replacement Cover")
              : t("component.reviewOrder.replacementLiner", "Replacement Liner")
          }
        />
        {state.product.partner && (
          <ReviewField
            value={state.product.partner}
            label={t("component.reviewOrder.purchasePartner", "Purchase Partner")}
          />
        )}
        {state.product.brand && (
          <ReviewField
            value={state.product.brand.name}
            label={t("component.reviewOrder.brand", "Brand")}
          />
        )}
        {type === OrderType.Cover && (
          <>
            {state.product.gridSpacing && (
              <ReviewField
                value={state.product.gridSpacing}
                label={t("component.reviewOrder.gridSpacing", "Grid Spacing")}
              />
            )}
            {state.product.material && (
              <ReviewField
                value={state.product.material}
                label={t("component.reviewOrder.material", "Material")}
              />
            )}
            {state.product.material !== chooseLaterString && (
              <ReviewField
                value={state.product.fabric}
                label={t("component.reviewOrder.fabric", "Fabric")}
              />
            )}
            {state.product.color && (
              <div className={"review-order__field"}>
                <div className={"review-order__field__label"}>
                  {t("component.reviewOrder.color", "Color")}
                </div>
                <img
                  className={"review-order__field__img"}
                  src={swatch?.url}
                  alt={state.product.color}
                />
                <div className={"review-order__field__value"}>{swatch?.color}</div>
              </div>
            )}
            {state.product.drainOrPump && (
              <ReviewField
                value={state.product.drainOrPump}
                label={t("component.reviewOrder.drainOrPump", "Drain or pump")}
              />
            )}
            {state.product.pumpQty && (
              <ReviewField
                value={state.product.pumpQty}
                label={t("component.reviewOrder.pumpQuantity", "Pump Quantity")}
              />
            )}
            {state.product.commercialSprings && (
              <ReviewField
                value={state.product.commercialSprings}
                label={t(
                  "component.reviewOrder.commercialSprings",
                  "Commercial springs",
                )}
              />
            )}
          </>
        )}
        {type === OrderType.Liner && (isAfterQuote || isAfterOrder) && (
          <>
            {state.product.material && (
              <ReviewField
                value={state.product.material}
                label={t("component.reviewOrder.material", "Material")}
              />
            )}
            {state.product.millGauge && (
              <ReviewField
                value={state.product.millGauge}
                label={t(
                  "component.forms.product.materialThickness",
                  "Material Thickness",
                )}
              />
            )}

            {state.product.bottomMillGauge && (
              <ReviewField
                value={state.product.bottomMillGauge}
                label={t(
                  "component.reviewOrder.bottomMaterialThickness",
                  "Bottom Material Thickness",
                )}
              />
            )}
            {state.product.linerPattern && (
              <ReviewField
                value={state.product.linerPattern}
                label={t("component.reviewOrder.linerPattern", "Liner Pattern")}
              />
            )}
            {state.product.linerBottomPattern && (
              <ReviewField
                value={state.product.linerBottomPattern}
                label={t(
                  "component.reviewOrder.linerBottomPattern",
                  "Liner Bottom Pattern",
                )}
              />
            )}
            {state.product.bead && (
              <ReviewField
                value={state.product.bead}
                label={t("component.reviewOrder.bead", "Bead")}
              />
            )}
          </>
        )}
        {type === OrderType.Liner && (
          <>
            <ReviewField
              value={state.product.overlap}
              label={t("component.reviewOrder.overlap", "Overlap")}
            />
            {state.product.overlapSize && (
              <ReviewField
                value={state.product.overlapSize}
                label={t("component.reviewOrder.overlapComments", "Overlap Comments")}
              />
            )}
          </>
        )}
      </div>
      <NativeModal basic open={loading}>
        <NativeModal.Content className={"scroll"}>
          <div className={"ui large text loader"}>
            {t("component.reviewOrder.loading", "Loading...")}
          </div>
        </NativeModal.Content>
      </NativeModal>

      <div className={"review-order__fields review-order__fields--features"}>
        <div className={"review-order__sub-title"}>
          {t("component.reviewOrder.features", "Features")}
        </div>
        {state?.features?.features?.length ? (
          state?.features?.features?.map((feature, index) => {
            return (
              <span key={index}>
                <div className={"review-order__features--title"}>
                  {feature.featureName}
                </div>
                {feature?.notes && feature.notes.length > 0 ? (
                  noteControl(feature.notes)
                ) : (
                  <div />
                )}
                {feature.featureType === FeatureType.AutoCover &&
                  feature.coverExtension && (
                    <ReviewField
                      value={feature.coverExtension}
                      label={"Safety Cover Options (Autocover)"}
                    />
                  )}
                {type === OrderType.Liner &&
                  (feature.featureType === FeatureType.VinylFeature ||
                    getFeatureType(feature.featureLabel ?? "") ===
                      FeatureType.VinylFeature) && (
                    <>
                      <ReviewField
                        value={
                          SameAsFloor.toLocaleLowerCase().includes(
                            feature?.stepMaterialOption?.toLocaleLowerCase() ??
                              ChooseLaterString,
                          )
                            ? SameAsFloor
                            : (feature.stepMaterialOption ?? undefined)
                        }
                        label={"Step Material Option"}
                      />
                      {feature.stepMaterial &&
                        (isAfterQuote || isAfterOrder) &&
                        feature.stepMaterial.length > 0 && (
                          <ReviewField
                            value={feature.linerStepMaterial?.label}
                            label={"Step Material"}
                          />
                        )}
                      <ReviewField
                        value={
                          getStepFastenerDisplayName(feature.stepFastener) ?? undefined
                        }
                        label={"Step Fastener Type"}
                      />
                      {feature.stepComment && feature.stepComment.length > 0 && (
                        <TreadListControl
                          value={getTreadComment(feature.stepComment, feature)}
                        />
                      )}

                      <ReviewField
                        value={feature.stepStripe ?? undefined}
                        label={"White stripe on each tread"}
                      />
                      {feature.stepStripe === YES && (
                        <ReviewField
                          value={feature.stripeWidth ?? undefined}
                          label={"Stripe Width"}
                        />
                      )}
                    </>
                  )}
                {type === OrderType.Liner &&
                  (feature.featureType === FeatureType.MoldedStep ||
                    getFeatureType(feature.featureLabel ?? "") ===
                      FeatureType.MoldedStep) && (
                    <>
                      <ReviewField
                        value={feature.moldedStepFace?.toString() ?? undefined}
                        label={"Step Face"}
                      />
                    </>
                  )}
                {type === OrderType.Cover && (
                  <>
                    {feature.anchorTypeOptions &&
                      !isOptionsHidden(feature.featureType) && (
                        <ReviewField
                          value={feature.anchorTypeOptions ?? undefined}
                          label={"Anchor Type"}
                        />
                      )}
                    {feature.cableAssembly != undefined &&
                      !isOptionsHidden(feature.featureType) && (
                        <ReviewField
                          value={feature.cableAssembly ?? undefined}
                          label={"Cable Assembly"}
                        />
                      )}
                    {(isFeatureRemovable(feature.featureType) ||
                      feature.featureType === FeatureType.Other) && (
                      <ReviewField
                        value={feature.isRemovable ?? undefined}
                        label={"Is Removable?"}
                      />
                    )}
                    {feature.featureType === FeatureType.Spa && (
                      <ReviewField
                        value={feature.isCovered ?? undefined}
                        label={"Is Covered?"}
                      />
                    )}
                    {feature.featureType === FeatureType.DivingObject && (
                      <ReviewField
                        value={feature?.isCovered ? YES : NO}
                        label={"Does object extend below deck?"}
                      />
                    )}
                  </>
                )}
              </span>
            );
          })
        ) : (
          <div>
            {t(
              "component.reviewOrder.noFeaturesTagged",
              "No features were tagged during measuring.",
            )}
          </div>
        )}
      </div>

      {type === OrderType.Cover && (
        <div className={"review-order__fields review-order__fields--anchors"}>
          <div className={"review-order__sub-title"}>
            {t("component.reviewOrder.anchors", "Anchors")}
          </div>
          {state?.anchors.decks &&
            state.anchors.decks.map((anchor, index) => (
              <span key={index}>
                <div className={"review-order__features--title"}>
                  {state.decksData[index]?.label ?? "Decking"}
                </div>
                {anchor.decking && (
                  <ReviewField
                    value={anchor.decking}
                    label={t("component.reviewOrder.deckingType", "Decking Type")}
                  />
                )}
                {anchor.hardware && (
                  <ReviewField
                    value={anchor.hardware}
                    label={t("component.reviewOrder.hardware", "Hardware")}
                  />
                )}
                {anchor.collars && (
                  <ReviewField
                    value={anchor.collars}
                    label={t("component.reviewOrder.collars", "Collars")}
                  />
                )}
                {anchor.padding && (
                  <ReviewField
                    value={anchor.padding}
                    label={t("component.reviewOrder.padding", "Padding")}
                  />
                )}
                {anchor.paddingType && (
                  <ReviewField
                    value={anchor.paddingType}
                    label={t("component.reviewOrder.paddingType", "Padding Type")}
                  />
                )}
              </span>
            ))}
        </div>
      )}

      <div className={"review-order__fields review-order__fields--accessories"}>
        <div className={"review-order__sub-title"}>
          {t("component.reviewOrder.accessories", "Accessories")}
        </div>
        {type === OrderType.Cover && (
          <>
            <ReviewField
              value={state.accessories.doubleD_Rings}
              label={t("component.reviewOrder.doubleDRings", "Double D-Rings")}
            />
            <ReviewField
              value={state.accessories.topAndBottomWebbing}
              label={t(
                "component.reviewOrder.topAndBottomWebbing",
                "Top and Bottom Webbing",
              )}
            />
            <ReviewField
              value={state.accessories.buckles}
              label={t("component.reviewOrder.buckles", "Buckles")}
            />
            <ReviewField
              value={totalYStrapQty > 0}
              label={t("component.reviewOrder.yStrap", "Y Strap")}
            />
            {/* Display Y strap quantity from features */}
            {totalYStrapQty > 0 && (
              <ReviewField
                value={String(totalYStrapQty)}
                label={t("component.reviewOrder.yStrapQty", "Y Strap Qty")}
              />
            )}
          </>
        )}
        {type === OrderType.Liner && (
          <>
            <ReviewField
              value={state.accessories.divingTarget}
              label={t("component.reviewOrder.divingTarget", "Diving Target")}
            />
            {state.accessories.divingTargetComments && (
              <ReviewField
                value={state.accessories.divingTargetComments}
                label={t(
                  "component.reviewOrder.divingTargetComments",
                  "Diving Target Comments",
                )}
              />
            )}
            <ReviewField
              value={state.accessories.racingStripes}
              label={t("component.reviewOrder.racingStripes", "Racing Stripes")}
            />
            {state.accessories.racingStripesComments && (
              <ReviewField
                value={state.accessories.racingStripesComments}
                label={t(
                  "component.reviewOrder.racingStripesComments",
                  "Racing Stripes Comments",
                )}
              />
            )}
            {state.accessories.racingStripesCount && (
              <ReviewField
                value={state.accessories.racingStripesCount}
                label={t(
                  "component.reviewOrder.racingStripesCount",
                  "Racing Stripes Count",
                )}
              />
            )}
            <ReviewField
              value={state.accessories.safetyStripes}
              label={t("component.reviewOrder.safetyStripes", "Safety Stripes")}
            />
            {state.accessories.safetyStripes &&
              state.accessories.safetyStripesColor && (
                <ReviewField
                  value={state.accessories.safetyStripesColor}
                  label={t(
                    "component.reviewOrder.safetyStripesColor",
                    "Safety Stripes Color",
                  )}
                />
              )}
            {state.accessories.safetyStripes && state.accessories.safetyStripeWidth && (
              <ReviewField
                value={state.accessories.safetyStripeWidth}
                label={t(
                  "component.reviewOrder.safetyStripeWidth",
                  "Safety Stripe Width",
                )}
              />
            )}
            <ReviewField
              value={state.accessories.solidShallowEnd}
              label={t("component.reviewOrder.solidShallowEnd", "Solid Shallow End")}
            />
            {state.accessories.solidShallowEndColor && (
              <ReviewField
                value={state.accessories.solidShallowEndColor}
                label={t(
                  "component.reviewOrder.solidShallowEndColor",
                  "Solid Shallow End Color",
                )}
              />
            )}
          </>
        )}
      </div>
      <div className={"review-order__fields review-order__fields--shipping"}>
        {hasValueForShipping && (
          <div className={"review-order__sub-title"}>
            {t("component.reviewOrder.shipping", "Shipping")}
          </div>
        )}
        {state.shipping.whereToShip && (
          <ReviewField
            value={state.shipping.whereToShip}
            label={t("component.reviewOrder.shippingTo", "Shipping to:")}
          />
        )}
        {state.shipping.name && (
          <ReviewField
            value={state.shipping.name}
            label={t("component.common.name", "Name")}
          />
        )}
        {state.shipping.addressLine1 && (
          <ReviewField
            value={state.shipping.addressLine1}
            label={t("component.common.address", "Address")}
          />
        )}
        {state.shipping.addressLine2 && (
          <ReviewField
            value={state.shipping.addressLine2}
            label={t("component.common.addressCont", "Address (cont.)")}
          />
        )}
        {state.shipping.city && (
          <ReviewField
            value={state.shipping.city}
            label={t("component.common.city", "City")}
          />
        )}
        {state.shipping.shippingState && (
          <ReviewField
            value={state.shipping.shippingState}
            label={t("component.common.state", "State")}
          />
        )}
        {state.shipping.zip && (
          <ReviewField
            value={state.shipping.zip}
            label={t("component.common.zip", "ZIP")}
          />
        )}
      </div>
      {(state.order?.status === OrderStatus.Saved || isAfterQuote) && (
        <div className={"review-order__delete"}>
          <Button
            basic
            color={"red"}
            icon={"trash"}
            content={
              isAfterQuote
                ? t("component.reviewOrder.deleteOrder", "Delete Order")
                : t("component.reviewOrder.cancelQuote", "Cancel Quote")
            }
            onClick={() => {
              setIsConfirmModalOpen(true);
            }}
          />
          <Confirm
            size={"mini"}
            open={isConfirmModalOpen}
            header={
              isAfterQuote
                ? t("component.reviewOrder.aboutToDeleteOrder", "About to Delete Order")
                : t("component.reviewOrder.aboutToCancelQuote", "About to Cancel Quote")
            }
            content={t("component.reviewOrder.areYouSure", "Are you sure?")}
            onConfirm={() => handleDeleteOrder()}
            confirmButton={
              isAfterQuote
                ? t("component.common.delete", "Delete")
                : t("component.reviewOrder.cancelQuote", "Cancel Quote")
            }
            onCancel={() => {
              setIsConfirmModalOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
};

export const ReviewField = (props: {
  label: string;
  value: boolean | string | undefined;
}) => {
  const t = useT();
  return (
    <div className={"review-order__field"}>
      <div className={"review-order__field__label"}>{props.label}</div>
      {typeof props.value === "boolean" && (
        <div className={"review-order__field__value"}>
          {props.value
            ? t("component.common.yes", "Yes")
            : t("component.common.no", "No")}
        </div>
      )}
      {typeof props.value !== "boolean" && (
        <div className={"review-order__field__value"}>{props?.value}</div>
      )}
    </div>
  );
};

const TreadItem: FC<{ item: { key: string; value: string } }> = memo((props) => {
  const { item } = props;
  return (
    <div>
      <ReviewField label={item.key} value={item.value} />
    </div>
  );
});
TreadItem.displayName = "TreadItem";

export const TreadListControl = (props: {
  value: { key: string; value: string }[];
}) => {
  return (
    <div>
      {props.value.map((data) => (
        <TreadItem key={data.key} item={data} />
      ))}
    </div>
  );
};
